import type { DisciplineStore } from '@stores/discipline/interfaces'
import type { DisciplineInfo } from '@brighterly/lib-core-types/discipline/interfaces'
import type { TutorContact } from '@brighterly/lib-core-types/tutor-contact/interfaces'
import type { ScopeTarget } from '@ts-scope/interfaces'
import type { ScopeMode } from '@utils/stores/scoped-entry'

import { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { useKidStore } from '@stores/kid'

import { beginEntry, entryData, failEntry, settleEntry } from '@utils/stores/scoped-entry'

import { getActiveDiscipline } from './getters'
import { createInitialState } from './state'

interface ScopeChange {
  changed: boolean
}

const activeInfo = (state: DisciplineStore): DisciplineInfo | null =>
  entryData(state.infoById, getActiveDiscipline(state)?.id ?? null)

const setLanding = (state: DisciplineStore, code: Discipline, value: boolean): void => {
  if (code === Discipline.Math) state.mathLanding = value
  if (code === Discipline.ELA) state.elaLanding = value
}

const setScope = (state: DisciplineStore, target: ScopeTarget): ScopeChange => {
  const kidStore = useKidStore()
  const changed = kidStore.activeKidId !== target.kidId || state.activeCode !== target.code

  if (kidStore.activeKidId !== target.kidId) kidStore.setActiveKid(target.kidId)
  state.activeCode = target.code

  if (changed) setLanding(state, target.code, false)

  if (state.pendingLanding === target.code) {
    setLanding(state, target.code, true)
    state.pendingLanding = null
  }

  return { changed }
}

export const actions = {
  resetState (this: DisciplineStore): void {
    Object.assign(this, createInitialState())
  },
  setScope (this: DisciplineStore, target: ScopeTarget): ScopeChange {
    return setScope(this, target)
  },
  setPendingLanding (this: DisciplineStore, code: DisciplineStore['pendingLanding']): void {
    this.pendingLanding = code
  },
  clear (this: DisciplineStore): void {
    this.activeCode = null
    this.pendingLanding = null
    this.mathLanding = false
    this.elaLanding = false
  },
  setMathLanding (this: DisciplineStore, payload: DisciplineStore['mathLanding']): void {
    this.mathLanding = payload
  },
  setElaLanding (this: DisciplineStore, payload: DisciplineStore['elaLanding']): void {
    this.elaLanding = payload
  },
  beginInfo (this: DisciplineStore, id: number, mode: ScopeMode): number {
    return beginEntry(this.infoById, id, mode)
  },
  settleInfo (this: DisciplineStore, id: number, requestId: number, data: DisciplineInfo): void {
    settleEntry(this.infoById, id, requestId, data)
  },
  failInfo (this: DisciplineStore, id: number, requestId: number): void {
    failEntry(this.infoById, id, requestId)
  },
  setDisciplineNewFiles (this: DisciplineStore, payload: number): void {
    const info = activeInfo(this)
    if (info) info.files_new = payload
  },
  setTutorContact (this: DisciplineStore, payload: TutorContact): void {
    const info = activeInfo(this)
    if (info) info.tutor_contact = payload
  },
  setModalTutorProfile (this: DisciplineStore, payload: DisciplineStore['modalTutorProfile']): void {
    this.modalTutorProfile = payload
  },
}
